const {V} = require("./VectorDraw");
const _ = require("lodash");
const Common = require("./CommonHelpers");
const ArrowDraw = require("../../util/ArrowDraw");
const D = Common.D;
const colors = require("../../util/ColorUtil").getDiagramColors();
const Font = require("../../util/Font");
const GridUtil = require("../../util/GridUtil");

//Creates a shape area on the context, ctx, in the rectangle specified by top left x,y with width w and height h
//It will be an area of size gridWidth by gridHeight (and will not lose its aspect ratio to fit the given rectangle, will be centered)
//The returned shape area then has functions that can be called that allow the use of easier coordinates the creation of simpler labels.
//options can be provided that override gridWidth and gridHeight by providing a startX, startY, endX, and endY
module.exports = function(ctx, x, y, w, h, gridWidth, gridHeight, gridOpts) {
	let startX = 0;
	let startY = 0;
	const angleSizes = new Map();
	if (typeof gridWidth === "object") {
		startX = gridWidth.startX;
		startY = gridWidth.startY;
		const {endX, endY} = gridWidth;
		gridWidth = endX - startX;
		gridHeight = endY - startY;
	}
	const realW = Math.min(w, (h * gridWidth) / gridHeight);
	const realH = (realW * gridHeight) / gridWidth;
	const realX = x + (w - realW) / 2;
	const realY = y + (h - realH) / 2;

	//translates a point to the new coordinate system defined by the shape area, an internal shape area function
	const tr = function(p) {
		return V(
			realX + ((p[0] - startX) * realW) / gridWidth,
			realY + ((gridHeight - (p[1] - startY)) * realH) / gridHeight
		);
	};

	//generates coordinates for a regular shape given its number of sides, its center point, and its radius
	//The rotation of the shape is optional.
	const generateRegularCoordinates = function(
		numSides,
		center,
		radius,
		rotation
	) {
		const coordinates = [];
		let theta = -1 * rotation || 0;
		center = V(center);
		for (let i = 0; i < numSides; i++) {
			coordinates.push(center.add(V(0, radius).rotateDeg(theta)));
			theta -= 360 / numSides;
		}
		return coordinates;
	};

	const generateGrid = function(
		type,
		cols,
		rows,
		{bottomLeft = [0, 0], unitSize = 1} = {}
	) {
		type = type.toLowerCase();
		if (type === "triangular" || type === "triangular1") {
			cols = cols || 5;
			rows = rows || 4;
		} else if (type === "triangular2") {
			cols = cols || 6;
			rows = rows || 4;
		} else if (type === "square") {
			cols = cols || 4;
			rows = rows || 4;
		} else if (type === "rectangle") {
			cols = cols || 5;
			rows = rows || 4;
		} else if (type === "tiltedSquare1" || type === "tiltedSquare2") {
			cols = cols || 3;
			rows = rows || 3;
		}
		const gridData = GridUtil.generateGrid(type, {
			rows,
			cols,
			scaleFactor: unitSize,
			translation: bottomLeft, // translation is AFTER scaling points
		});

		const grid = {allPoints: gridData.points.map(p => V(p))};

		// Set grid properties like mapping indices so grid points
		// can be accessed like the array
		gridData.pointMapArray.forEach((col, gridX) => {
			grid[gridX] = [];
			col.forEach((point, gridY) => (grid[gridX][gridY] = point));
		});

		return grid;
	};

	//draws a shape by connecting each of the points with the given settings
	//possible settings:
	//stroke: the color of the shape's outline, null/false if it should not have an outline (defaults to default black)
	//fill: the color of the shape's fill, null/false if it should not have a fill, defaults to not filled
	//lineWidth: how thick the outline should be
	//isDashed: true if the outline should be dashed
	//gridLines: true if grid lines should appear constrained to this shape
	//gridLineColor: the color of those gridLines, defaults to medium light gray
	//rightAngles: true if all right angles in the shape should get a right angle symbol
	//dots: true if the vertices should be dotted
	//dotColor: the color the vertices should be dotted with
	const shape = function(points, settings = {}) {
		let minAngleSize = Infinity;
		_.forEach(points, (point, i) => {
			minAngleSize = Math.min(
				calculateRightAngleSize(
					points[i],
					points[(i + 1) % points.length],
					points[(i + 2) % points.length]
				),
				minAngleSize
			);
		});
		_.forEach(points, (point, i) => {
			const hashString = [
				points[i][0],
				points[i][1],
				points[(i + 1) % points.length][0],
				points[(i + 1) % points.length][1],
				points[(i + 2) % points.length][0],
				points[(i + 2) % points.length][1],
			].join(",");
			angleSizes.set(hashString, minAngleSize);
		});
		ctx.save();
		ctx.bp().M(tr(points[0]));
		_.forEach(points, (point, i) => {
			if (i === 0) {
				ctx.M(tr(point));
			} else {
				ctx.L(tr(point));
			}
		});
		ctx.Z();
		if (settings.hasOwnProperty("fill")) {
			ctx.fillStyle = settings.fill;
			ctx.fill();
		}
		ctx.save();
		if (settings.hasOwnProperty("lineWidth")) {
			ctx.lineWidth = settings.lineWidth;
		}
		if (settings.isDashed) {
			ctx.setLineDash([ctx.lineWidth * 5]);
		}
		if (
			(!settings.hasOwnProperty("stroke") || settings.stroke) &&
			!settings.gridLines
		) {
			ctx.strokeStyle = settings.stroke || colors.defaultblack;
			ctx.stroke();
		}
		ctx.restore();

		if (settings.rightAngles) {
			_.forEach(points, (point, i) => {
				const p1 = points[(points.length + i - 1) % points.length];
				const p2 = point;
				const p3 = points[(i + 1) % points.length];
				if (
					Math.abs(
						(p1[0] - p2[0]) * (p3[0] - p2[0]) +
							(p1[1] - p2[1]) * (p3[1] - p2[1])
					) <= 0.0001
				) {
					rightAngle(p1, p2, p3);
				}
			});
		}

		if (settings.gridLines) {
			ctx.save();
			ctx.clip();
			ctx.bp();
			ctx.strokeStyle = settings.gridLineColor || "rgba(100,100,100,0.5)";
			for (let j = 0; j <= gridHeight; j++) {
				ctx.M(tr([0, j])).L(tr([gridWidth, j]));
			}
			for (let k = 0; k <= gridWidth; k++) {
				ctx.M(tr([k, 0])).L(tr([k, gridHeight]));
			}
			ctx.stroke();
			ctx.restore();

			//Redrawing the outline over the gridLines if there is one
			if (!settings.hasOwnProperty("stroke") || settings.stroke) {
				ctx.save();
				ctx.bp();
				_.forEach(points, (point, i) => {
					if (i === 0) {
						ctx.M(tr(point));
					} else {
						ctx.L(tr(point));
					}
				});
				ctx.Z();
				if (settings.hasOwnProperty("lineWidth")) {
					ctx.lineWidth = settings.lineWidth;
				}
				if (settings.isDashed) {
					ctx.setLineDash([ctx.lineWidth * 5]);
				}
				ctx.strokeStyle = settings.stroke || colors.defaultblack;
				ctx.stroke();
				ctx.restore();
			}
		}
		if (settings.dots) {
			const dotSetting = settings.dotColor ? {color: settings.dotColor} : null;
			const dottedSoFar = [];
			_.forEach(points, point => {
				if (
					_.findIndex(dottedSoFar, p => p[0] === point[0] && p[1] === point[1])
				) {
					dot(point, dotSetting);
					dottedSoFar.push(point);
				}
			});
		}
		ctx.restore();
	};

	//draws a path by connecting each of the points with the given settings
	//possible settings:
	//stroke: the color of the shape's outline, null/false if it should not have an outline (defaults to default black)
	//lineWidth: how thick the outline should be
	//isDashed: true if the outline should be dashed
	//rightAngles: true if all right angles in the shape should get a right angle symbol
	//dots: true if all points on the path should get dots
	//dotColor: color that the point dots should get
	const path = function(points, settings) {
		settings = settings || {};
		ctx.save();
		ctx.bp().M(tr(points[0]));
		let i;
		for (i = 1; i < points.length; i++) {
			ctx.L(tr(points[i]));
		}
		ctx.save();
		if (settings.hasOwnProperty("lineWidth")) {
			ctx.lineWidth = settings.lineWidth;
		}
		if (settings.hasOwnProperty("lineCap")) {
			ctx.lineCap = settings.lineCap;
		}
		if (settings.isDashed) {
			ctx.setLineDash([ctx.lineWidth * 5]);
		}
		if (!settings.hasOwnProperty("stroke") || settings.stroke) {
			ctx.strokeStyle = settings.stroke || colors.defaultblack;
			ctx.stroke();
		}
		ctx.restore();
		if (settings.rightAngles) {
			for (i = 0; i < points.length; i++) {
				const p1 = points[(points.length + i - 1) % points.length];
				const p2 = points[i];
				const p3 = points[(i + 1) % points.length];
				if (
					(p1[0] - p2[0]) * (p3[0] - p2[0]) +
						(p1[1] - p2[1]) * (p3[1] - p2[1]) ===
					0
				) {
					rightAngle(p1, p2, p3);
				}
			}
		}
		if (settings.dots) {
			const dotSetting = settings.dotColor ? {color: settings.dotColor} : null;
			const dottedSoFar = [];
			for (i = 0; i < points.length; i++) {
				if (
					_.findIndex(
						dottedSoFar,
						p => p[0] === points[i][0] && p[1] === points[i][1]
					)
				) {
					dot(points[i], dotSetting);
					dottedSoFar.push(points[i]);
				}
			}
		}
		ctx.restore();
	};

	// draws a line by connecting p1 to p2
	// possible settings:
	// - stroke: the color of the line, defaults to default black
	// - lineWidth: how thick the line should be
	// - isDashed: true if the line should be dashed
	// - arrows: array of 2 values. If first is number, draw arrowhead at p1. If
	//   second is number, draw arrowhead at p2. The number gives length to
	//   extend line by.
	const line = function(point1, point2, settings = {}) {
		ctx.save();
		ctx
			.bp()
			.M(tr(point1))
			.L(tr(point2));
		if (settings.hasOwnProperty("lineWidth")) {
			ctx.lineWidth = settings.lineWidth;
		}
		if (settings.isDashed) {
			ctx.setLineDash([ctx.lineWidth * 5]);
		}
		ctx.strokeStyle = settings.stroke || colors.defaultblack;
		ctx.stroke();
		if (settings.arrows) {
			const {headLength} = Common.getArrowDimensions(ctx);
			const color =
				settings.arrowColor || settings.stroke || colors.defaultblack;
			ctx.strokeStyle = color;
			ctx.fillStyle = color;

			const drawArrowFromLine = (p1, p2, extraLength) => {
				//Find the line from p1 to p2 and extends it extraLength units past p2 to arrowPoint
				const diff = V(p2).minus(V(p1));
				const arrowPoint = V(p2).add(
					diff.scale(extraLength / diff.magnitude())
				);
				let start = tr(p2);
				const end = tr(arrowPoint);
				ctx
					.bp()
					.M(start)
					.L(end)
					.stroke();
				if (extraLength === 0) {
					//set start to p1 before calculating arrow head positions
					//since the start and end coordinates cannot be equal
					start = tr(p1);
				}
				ctx.save();
				ctx.setLineDash([]); //Undo line dash if any
				//Draws an arrow placed on the line from p1 to arrowPoint with the tip at arrowPoint
				ArrowDraw.drawArrowHead(
					ctx,
					ArrowDraw.calculateArrowHeadPositions({
						startX: start[0],
						startY: start[1],
						endX: end[0],
						endY: end[1],
						headLength: headLength * 0.55,
						headHeight: headLength * 0.45,
						curved: true,
					})
				);
				ctx.restore();
			};
			if (_.isNumber(settings.arrows[0])) {
				drawArrowFromLine(point2, point1, settings.arrows[0]);
			}
			if (_.isNumber(settings.arrows[1])) {
				drawArrowFromLine(point1, point2, settings.arrows[1]);
			}
		}
		ctx.restore();
	};

	//draws a line by connecting point1 to point2 that has marks at the ends and the given batex as the label
	//possible settings:
	//stroke: the color of the line, defaults to default black
	//lineWidth: how thick the line should be
	//labelSide: can be "above", "below", "left", or "right" (defaults to above)
	//labelColor: the color of the label, defaults to default black
	const measureLine = function(point1, point2, batex, settings) {
		settings = settings || {};
		ctx.save();
		const p1 = tr(point1);
		const p2 = tr(point2);
		const markSize = ctx.lineWidth * 6;
		const v = p1
			.sub(p2)
			.scale(markSize / p1.sub(p2).magnitude())
			.rotateDeg(90);
		ctx
			.bp()
			.M(p1.sub(v))
			.L(p1.add(v))
			.M(p1)
			.L(p2)
			.M(p2.sub(v))
			.L(p2.add(v));
		if (settings.hasOwnProperty("lineWidth")) {
			ctx.lineWidth = settings.lineWidth;
		}
		ctx.strokeStyle = settings.stroke || colors.defaultblack;
		ctx.stroke();
		if (
			!settings.labelSide ||
			settings.labelSide === "above" ||
			settings.labelSide === "below"
		) {
			label1(point1, point2, batex, {
				color: settings.labelColor,
				below: settings.labelSide === "below",
			});
		} else {
			const mPoint = V.mid(p1, p2);
			Common.label(
				ctx,
				batex,
				mPoint[0],
				mPoint[1],
				settings.labelSide === "left" ? D.W : D.E
			);
		}
		ctx.restore();
	};

	//draws a right angle sign at the angle from p1 to p2 to p3
	//possible settings:
	//stroke: the color of the angle lines, defaults to default black
	const rightAngle = function(point1, point2, point3, settings) {
		settings = settings || {};
		const p1 = tr(V(point1));
		const p2 = tr(V(point2));
		const p3 = tr(V(point3));
		const hashString = [
			point1[0],
			point1[1],
			point2[0],
			point2[1],
			point3[0],
			point3[1],
		].join(",");
		const hashStringBackwards = [
			point3[0],
			point3[1],
			point2[0],
			point2[1],
			point1[0],
			point1[1],
		].join(",");
		let angleSize;
		if (angleSizes.has(hashString)) {
			angleSize = angleSizes.get(hashString);
		} else if (angleSizes.has(hashStringBackwards)) {
			angleSize = angleSizes.get(hashStringBackwards);
		} else {
			angleSize = calculateRightAngleSize(point1, point2, point3);
		}
		const v1 = p1.sub(p2).scale(angleSize / p1.sub(p2).magnitude());
		const v2 = p3.sub(p2).scale(angleSize / p3.sub(p2).magnitude());

		ctx.save();
		ctx.lineWidth = ctx.lineWidth * 0.7;
		ctx.strokeStyle = settings.stroke || colors.defaultblack;
		ctx
			.bp()
			.M(p2.add(v1))
			.L(p2.add(v1).add(v2))
			.L(p2.add(v2));
		ctx.stroke();
		ctx.restore();
	};

	//calculate the size of a right angle sign. This size is not the final
	// size, which takes into account the rest of the right angles in the
	// shape.
	const calculateRightAngleSize = function(point1, point2, point3) {
		const p1 = tr(V(point1));
		const p2 = tr(V(point2));
		const p3 = tr(V(point3));
		const v1Unscaled = p1.sub(p2);
		const v2Unscaled = p3.sub(p2);
		const angleSize = Math.max(
			ctx.lineWidth * 8,
			Math.min(
				ctx.lineWidth * 12,
				v1Unscaled.magnitude() / 4,
				v2Unscaled.magnitude() / 4
			)
		);
		return angleSize;
	};

	//draws an angle mark at the angle from p1 to p2 to p3
	//possible settings:
	//stroke: the color of the angle lines, defaults to default black
	//color: alias for stroke
	//markDistance: the multiplier for the angle mark distance
	//label: the batex for the label
	//labelColor: the color of the label, if different from the color of the angle lines
	//fontScale: the multiplier for the label size
	//labelDistance: the multiplier for the label distance
	//clockwise: true if the angle should be clockwise
	//counterclockwise: true if the angle should be counterclockwise
	//doubleMark: true if two marks should be drawn instead of one
	const markAngle = function(point1, point2, point3, settings) {
		settings = settings || {};
		const p1 = tr(V(point1));
		const p2 = tr(V(point2));
		const p3 = tr(V(point3));
		const v1 = p1.sub(p2);
		const v2 = p3.sub(p2);
		// These are technically the negatives of the angles from p2 to p1 and
		// p2 to p3 since the y-axis is inverted, which is why the arc from p1
		// to p3 is drawn counterclockwise if angle23 - angle21 > pi, as
		// opposed to angle21 - angle23 > pi which you might expect.
		const angle21 = Math.atan2(v1[1], v1[0]);
		const angle23 = Math.atan2(v2[1], v2[0]);
		const angle = (angle23 - angle21 + 4 * Math.PI) % (2 * Math.PI);
		let angleDir = angle > Math.PI;
		if (settings.clockwise) {
			angleDir = false;
		} else if (settings.counterclockwise) {
			angleDir = true;
		}
		const markDistance = !_.isUndefined(settings.markDistance)
			? settings.markDistance
			: 1;
		let angleSize =
			markDistance *
			Math.max(
				ctx.lineWidth * 6,
				Math.min(ctx.lineWidth * 12, v1.magnitude() / 4, v2.magnitude() / 4)
			);
		if (
			(!angleDir && angle < Math.PI / 4) ||
			(angleDir && angle > (7 * Math.PI) / 4)
		) {
			angleSize *= 12 / 8;
		}
		ctx.save();
		ctx.lineWidth = ctx.lineWidth * 0.7;
		const color = settings.stroke || settings.color || colors.defaultblack;
		ctx.strokeStyle = color;
		if (settings.doubleMark) {
			const angleSizeA = (angleSize * 7) / 8;
			const angleSizeB = (angleSize * 9) / 8;
			ctx.bp().arc(p2[0], p2[1], angleSizeA, angle21, angle23, angleDir);
			ctx.stroke();
			ctx.bp().arc(p2[0], p2[1], angleSizeB, angle21, angle23, angleDir);
			ctx.stroke();
		} else {
			ctx.bp().arc(p2[0], p2[1], angleSize, angle21, angle23, angleDir);
			ctx.stroke();
		}
		if (settings.label) {
			const labelRotation = angleDir
				? angle23 + (Math.PI * 2 - angle) / 2
				: angle21 + angle / 2;
			const labelDistance = !_.isUndefined(settings.labelDistance)
				? settings.labelDistance
				: 1;
			const labelLoc = p2.add(
				V(angleSize * 2.2 * labelDistance, 0).rot(labelRotation)
			);
			ctx.fillStyle = settings.labelColor || color;
			let fontScale = settings.fontScale || 1;
			fontScale *= 0.8;
			ctx.font = Font.scaleSize(ctx.font, fontScale);
			Common.label(ctx, settings.label, labelLoc[0], labelLoc[1], [0, 0]);
		}
		ctx.restore();
	};

	//labels a single point with the given batex
	//possible settings:
	//color: the color of the label, defaults to default black
	//dir: the direction from the coordinate that the label should go (D.N, D.SE, etc.)
	const labelPoint = function(point, batex, settings) {
		settings = settings || {};
		ctx.save();
		const p = tr(point);
		ctx.fillStyle = settings.color || colors.defaultblack;
		Common.label(ctx, batex, p[0], p[1], settings.dir || D.N);
		ctx.restore();
	};

	//labels a line with the given batex in the middle of the line
	//possible settings:
	//color: the color of the label, defaults to default black
	//below: true if the label should go below the line rather than above it (defaults to false)
	//vertical: true if the label should be vertical rather than along the line (defaults to false)
	const label1 = function(p1, p2, batex, settings) {
		if (typeof p2 === "string") {
			labelPoint(p1, p2, batex);
			return;
		}
		settings = settings || {};
		ctx.save();
		ctx.fillStyle = settings.color || colors.defaultblack;
		p1 = tr(p1);
		p2 = tr(p2);
		const mPoint = V.mid(p1, p2);
		let direction = settings.below ? D.S : D.N;
		if (settings.vertical) {
			const dist = Math.pow(
				Math.pow(p2[1] - p1[1], 2) + Math.pow(p2[0] - p1[0], 2),
				0.5
			);
			const mult = settings.below ? 0.7 : -0.7;
			direction = [
				(mult * Math.abs(p2[1] - p1[1])) / dist,
				(mult * Math.abs(p2[0] - p1[0])) / dist,
			];
			if ((p2[1] - p1[1]) * (p2[0] - p1[0]) > 0) {
				direction[0] *= -1;
			}
		} else {
			ctx.rotateAbout(
				(3 * Math.PI) / 2 - Math.atan2(p1[0] - mPoint[0], p1[1] - mPoint[1]),
				mPoint[0],
				mPoint[1]
			);
		}
		Common.label(ctx, batex, mPoint[0], mPoint[1], direction);
		ctx.restore();
	};

	//used for internal generators
	const labelOutside = function(p1, p2, polygonCenter, batex) {
		ctx.save();
		ctx.fillStyle = colors.defaultblack;
		const mPoint = V.mid(p1, p2);
		const dist = Math.pow(
			Math.pow(mPoint[1] - polygonCenter[1], 2) +
				Math.pow(mPoint[0] - polygonCenter[0], 2),
			0.5
		);
		const mult = 0.7;
		const direction = [
			(mult * (mPoint[0] - polygonCenter[0])) / dist,
			(mult * (polygonCenter[1] - mPoint[1])) / dist,
		];
		const trmp = tr(mPoint);
		Common.label(ctx, batex, trmp[0], trmp[1], direction);
		ctx.restore();
	};

	//Draws dots at every integer x, y coordinate in the area (for use in 3-12-7)
	//possible settings:
	//color: the color of the dots, defaults to default black
	//large: true if the dots should be double size, defaults to false;
	const unitDots = function(settings) {
		settings = settings || {};
		ctx.save();
		ctx.fillStyle = settings.color || colors.defaultblack;
		for (let i = 0; i <= gridWidth; i++) {
			for (let j = 0; j <= gridHeight; j++) {
				const p = tr([i, j]);
				const radius = ctx.lineWidth * 3 * (settings.large ? 2 : 1);
				ctx
					.bp()
					.arc(p[0], p[1], radius, 0, 2 * Math.PI)
					.fill();
			}
		}
		ctx.restore();
	};

	//Draws a dot at the specified point
	//possible settings:
	//color: the color of the dots, defaults to default black
	//large: true if the dots should be double size, defaults to false;
	const dot = function(point, settings) {
		settings = settings || {};
		ctx.save();
		ctx.fillStyle = settings.color || colors.defaultblack;
		const p = tr(point);
		let multiplier = 1;
		if (settings.small) multiplier = 0.6;
		if (settings.large) multiplier = 2;
		const radius = ctx.lineWidth * 3 * multiplier;
		ctx
			.bp()
			.arc(p[0], p[1], radius, 0, 2 * Math.PI)
			.fill();
		ctx.restore();
	};

	//Draws dots at an array of points with the given settings
	//See dot function for settings
	const dots = function(points, settings) {
		points.forEach(pt => dot(pt, settings));
	};

	//Draws an arrow from the first specified point to the second
	//possible settings:
	//color: the color of the arrow
	//bothWays: true if there should be an arrow head on both sides
	const arrow = function(p1, p2, settings) {
		settings = settings || {};
		ctx.save();
		const start = tr(p1);
		const end = tr(p2);
		ctx.strokeStyle = settings.color || colors.defaultblack;
		ctx.fillStyle = settings.color || colors.defaultblack;
		Common.drawArrow(ctx, start[0], start[1], end[0], end[1]);
		if (settings.bothWays) {
			Common.drawArrow(ctx, end[0], end[1], start[0], start[1]);
		}
		ctx.restore();
	};

	//Draws a gridlines over the whole shape area
	//possible settings:
	//color: the color of the lines, defaults to medium light gray
	const gridLines = function(settings) {
		settings = settings || {};
		ctx.save();
		ctx.strokeStyle = settings.color || "rgba(100,100,100,0.5)";
		ctx.bp();
		for (let j = 0; j <= gridHeight; j++) {
			ctx.M(tr([0, j])).L(tr([gridWidth, j]));
		}
		for (let k = 0; k <= gridWidth; k++) {
			ctx.M(tr([k, 0])).L(tr([k, gridHeight]));
		}
		ctx.stroke();
		ctx.restore();
	};

	/**
	 * Create a circle with options centered at point with radius r
	 * stroke, lineWidth, and isDashed are the color, width, and status (dashed or solid) of the line around the circle and between sectors.
	 * fill is the color of the circle
	 * showParts is an array with showParts[0] indicating the numerator of the fraction of the circle that should be filled and showParts[1]
	 * indicating the denominator. showParts[2] indicates the number of sectors that should be skipped before the first filled one.
	 * If showParts is not provided, the entire circle will be filled. If showParts[2] is left out, the filling will start at the top.
	 **/
	const circle = function(point, r, settings) {
		settings = _.extend(
			{
				stroke: colors.defaultblack,
				fill: null,
				lineWidth: ctx.lineWidth,
				isDashed: false,
				showParts: [-1],
			},
			settings
		);
		const {stroke, fill, lineWidth, isDashed, showParts} = settings;
		const p = tr(point);
		r *= Math.min(realW / gridWidth, realH / gridHeight);
		ctx.save();
		if (isDashed) {
			ctx.setLineDash([lineWidth * 5]);
		}
		ctx.lineWidth = lineWidth;
		if (showParts[0] === -1) {
			ctx.bp().arc(p[0], p[1], r, 0, 2 * Math.PI);
			if (fill) {
				ctx.fill(fill);
			}
			ctx.stroke(stroke);
		} else {
			const skip = showParts.length > 2 ? showParts[2] : 0;
			const startingAngle =
				((skip + showParts[0]) / showParts[1] - 0.25) * 2 * Math.PI;
			const finalAngle =
				startingAngle + 2 * Math.PI * (1 - showParts[0] / showParts[1]);
			const xStart = p[0] + r * Math.cos(startingAngle);
			const yStart = p[1] + r * Math.sin(startingAngle);
			const xEnd = p[0] + r * Math.cos(finalAngle);
			const yEnd = p[1] + r * Math.sin(finalAngle);
			ctx
				.bp()
				.M(xStart, yStart)
				.arc(p[0], p[1], r, startingAngle, finalAngle)
				.stroke(stroke);
			ctx
				.bp()
				.M(xEnd, yEnd)
				.arc(p[0], p[1], r, finalAngle, startingAngle)
				.L(p[0], p[1])
				.L(xEnd, yEnd);
			if (fill) {
				ctx.fill(fill);
			}
			ctx.stroke(stroke);
			for (
				let angle = startingAngle;
				angle < startingAngle + 2 * Math.PI;
				angle += (2 * Math.PI) / showParts[1]
			) {
				if (angle !== startingAngle && angle !== finalAngle) {
					ctx
						.bp()
						.M(p[0], p[1])
						.l(r * Math.cos(angle), r * Math.sin(angle))
						.stroke(stroke);
				}
			}
		}
		ctx.restore();
	};

	return {
		shape,
		path,
		label: label1,
		labelOutside,
		line,
		unitDots,
		dot,
		dots,
		arrow,
		gridLines,
		rightAngle,
		markAngle,
		measureLine,
		generateGrid,
		generateRegularCoordinates,
		circle,
	};
};
